import React from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container, Typography, Box, Button, Accordion, AccordionSummary, AccordionDetails, Alert, Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SanitizedHtmlRenderer from '../components/SanitizedHtmlRenderer';

const getHtmlContent = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (data.text) return data.text;
    return '';
};

const getPlainText = (data) => {
    const html = getHtmlContent(data);
    return html.replace(/<[^>]*>?/gm, '');
};

const SearchResults = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { results, searchTerm } = location.state || {};

    const handleStartQuizFromResults = () => {
        navigate('/quiz-setup', { state: { searchResults: results } });
    };

    if (!results || results.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom>Search Results</Typography>
                <Alert severity="info" sx={{ mt: 3, justifyContent: 'center' }}>
                    No search results found. Please try another term from the
                    <RouterLink to="/"> homepage</RouterLink>.
                </Alert>
            </Container>
        );
    }

    return (
        // Added overflowX: hidden to container to prevent horizontal scroll on mobile
        <Container maxWidth="md" sx={{ py: 4, overflowX: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Search Results
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Found <strong>{results.length}</strong> question{results.length !== 1 && 's'} matching "<strong>{searchTerm}</strong>".
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartQuizFromResults}
                >
                    Create a Quiz from These Questions
                </Button>
            </Box>

            <Typography variant="h5" sx={{ mb: 2 }}>Review Questions</Typography>
            {results.map((question, index) => (
                <Accordion key={index} disableGutters>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                        // --- FIX START: Flexbox magic for the header ---
                        sx={{
                            '& .MuiAccordionSummary-content': {
                                width: '100%',
                                overflow: 'hidden', // Ensures flex child shrinks
                                display: 'flex',
                                alignItems: 'center'
                            }
                        }}
                        // --- FIX END ---
                    >
                        <Typography sx={{ flexShrink: 0, fontWeight: 'bold', mr: 2 }}>
                            Q{index + 1}
                        </Typography>
                        
                        {/* --- FIX START: Better text truncation --- */}
                        <Typography 
                            sx={{ 
                                color: 'text.secondary', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                flexGrow: 1, // Takes up remaining space
                                minWidth: 0  // Critical for Flexbox truncation to work
                            }}
                        >
                            {getPlainText(question.question)}
                        </Typography>
                        {/* --- FIX END --- */}
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}> {/* Less padding on mobile */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Full Question:
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                <SanitizedHtmlRenderer htmlContent={getHtmlContent(question.question)} />
                            </Box>

                            <Typography variant="subtitle1" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Options:
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                {question.options.map((option, optIndex) => (
                                    <Box 
                                        key={optIndex} 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'start', // Align to top in case option is long
                                            my: 1, 
                                            p: 1.5, 
                                            borderRadius: 1, 
                                            border: '1px solid', 
                                            borderColor: getPlainText(option) === getPlainText(question.answer) ? 'success.main' : 'divider',
                                            backgroundColor: getPlainText(option) === getPlainText(question.answer) ? 'rgba(46, 125, 50, 0.05)' : 'transparent'
                                        }}
                                    >
                                        {getPlainText(option) === getPlainText(question.answer) && (
                                            <CheckCircleOutlineIcon color="success" sx={{ mr: 1.5, mt: 0.5, flexShrink: 0 }} />
                                        )}
                                        {/* Container for option text to ensure it wraps */}
                                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                            <SanitizedHtmlRenderer htmlContent={getHtmlContent(option)} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ fontStyle: 'italic', backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Explanation:
                                </Typography>
                                <SanitizedHtmlRenderer htmlContent={getHtmlContent(question.explanation)} />
                            </Box>
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Container>
    );
};

export default SearchResults;
